import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';

import './Search.css';
import LazyCardImage from './LazyCardImage';
import { renderRarityBadge } from '../utils/rarityBadge';
import { GameContext } from '../contexts/GameContext';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { arrangeCardsForGrid } from '../utils/arrangeGrid';

var PAGE_SIZE = 24;

class Search extends Component {
  static contextType = GameContext;
  constructor(props) {
    super(props);
    this.state = {
      query: '',
      cardResults: [],
      cardsTotal: 0,
      loading: false,
      loadingMore: false,
      sortBy: 'name_asc',
      showScrollTop: false,
    };
    this.searchCards = this.searchCards.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollToTop = this.scrollToTop.bind(this);
  }

  componentDidMount() {
    var query = this.props.match.params.query;
    this.setState({ query: query }, function() {
      this.searchCards(query, false);
    });
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  componentWillReceiveProps(nextProps) {
    var prevQuery = this.props.match.params.query;
    var nextQuery = nextProps.match.params.query;
    if (nextQuery !== prevQuery) {
      this.setState({ query: nextQuery, sortBy: 'name_asc', cardResults: [], cardsTotal: 0 }, function() {
        this.searchCards(nextQuery, false);
      });
    }
  }

  handleScroll() {
    // Show/hide scroll-to-top button
    var showScrollTop = window.scrollY > 400;
    if (showScrollTop !== this.state.showScrollTop) {
      this.setState({ showScrollTop: showScrollTop });
    }

    // Infinite scroll: load more when near bottom
    if (this.state.loading || this.state.loadingMore) return;
    if (this.state.cardResults.length >= this.state.cardsTotal) return;

    var scrollBottom = window.innerHeight + window.scrollY;
    var threshold = document.documentElement.scrollHeight - 300;
    if (scrollBottom >= threshold) {
      this.searchCards(this.state.query, true);
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  searchCards(query, append) {
    var self = this;
    var skip = append ? self.state.cardResults.length : 0;

    self.setState(append ? { loadingMore: true } : { loading: true });

    var gameParam = (this.context && this.context.currentGame && this.context.currentGame.slug) ? '&game=' + encodeURIComponent(this.context.currentGame.slug) : '';
    fetch(constants.DOMAIN + '/api/v1/guest/cards/search?search=' + encodeURIComponent(query) + '&limit=' + PAGE_SIZE + '&skip=' + skip + gameParam, {
      mode: 'cors',
      method: 'GET',
    })
      .then(function(r) { return r.json(); })
      .then(function(res) {
        var newCards = res.cards || [];
        var allCards = append ? self.state.cardResults.concat(newCards) : newCards;
        self.setState({
          cardResults: allCards,
          cardsTotal: res.total || 0,
          loading: false,
          loadingMore: false,
        });
      })
      .catch(function(err) {
        console.log(err);
        self.setState({ loading: false, loadingMore: false });
      });
  }

  handleSortChange(e) {
    this.setState({ sortBy: e.target.value });
  }

  getSortedCards() {
    var cards = this.state.cardResults.slice();
    var sortBy = this.state.sortBy;

    if (sortBy === 'name_asc') {
      cards.sort(function(a, b) { return (a.name || '').localeCompare(b.name || ''); });
    } else if (sortBy === 'name_desc') {
      cards.sort(function(a, b) { return (b.name || '').localeCompare(a.name || ''); });
    } else if (sortBy === 'price_asc') {
      cards.sort(function(a, b) {
        if (a.lowestPrice == null && b.lowestPrice == null) return 0;
        if (a.lowestPrice == null) return 1;
        if (b.lowestPrice == null) return -1;
        return a.lowestPrice - b.lowestPrice;
      });
    } else if (sortBy === 'price_desc') {
      cards.sort(function(a, b) {
        if (a.lowestPrice == null && b.lowestPrice == null) return 0;
        if (a.lowestPrice == null) return 1;
        if (b.lowestPrice == null) return -1;
        return b.lowestPrice - a.lowestPrice;
      });
    } else if (sortBy === 'rarity') {
      var rarityOrder = { 'Common': 0, 'Uncommon': 1, 'Legacy Uncommon': 2, 'Rare': 3, 'Super Rare': 4, 'Legendary Rare': 5, 'Secret Rare': 6, 'Alternate Art': 7 };
      cards.sort(function(a, b) {
        var ra = rarityOrder[a.rarity] != null ? rarityOrder[a.rarity] : -1;
        var rb = rarityOrder[b.rarity] != null ? rarityOrder[b.rarity] : -1;
        return rb - ra;
      });
    }

    return cards;
  }

  render() {
    var sortedCards = this.getSortedCards();
    var hasMore = this.state.cardResults.length < this.state.cardsTotal;

    return (
      <div className="tw-min-h-screen tw-bg-white dark:tw-bg-[#111]">
        <Helmet>
          <title>Search results for {this.state.query} | Primal Market</title>
          <meta name="description" content={'Searching Primal Market for ' + this.state.query} />
          <meta property="og:title" content={'Search results for ' + this.state.query + ' | Primal Market'} />
          <meta property="og:description" content={'Searching Primal Market for ' + this.state.query} />
        </Helmet>

        <div className="tw-max-w-5xl tw-mx-auto tw-px-4 tw-py-8">
          {/* Header row with result count and sort */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
            <div>
              <h1 className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">
                Results for "<span className="tw-text-brand-gold">{this.state.query}</span>"
              </h1>
              <p className="tw-text-sm tw-text-[#999] dark:tw-text-[#666] tw-mb-0">
                {this.state.loading ? 'Searching...' : this.state.cardsTotal + ' card' + (this.state.cardsTotal !== 1 ? 's' : '') + ' found'}
              </p>
            </div>

            {this.state.cardResults.length > 1 && (
              <select
                value={this.state.sortBy}
                onChange={this.handleSortChange}
                className="tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-text-[#1a1a1a] dark:tw-text-white tw-cursor-pointer focus:tw-outline-none focus:tw-border-brand-gold"
              >
                <option value="name_asc">Name: A-Z</option>
                <option value="name_desc">Name: Z-A</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="rarity">Rarity: Highest First</option>
              </select>
            )}
          </div>

          {/* Card grid */}
          {!this.state.loading && sortedCards.length > 0 && (
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-5 tw-gap-4 tw-items-start">
              {arrangeCardsForGrid(sortedCards, 4).map(function(card) {
                var imageUrl = resolveCardImage(card.image);
                var isHoriz = card.horizontal === true;
                return (
                  <Link
                    key={card._id}
                    to={'/card/' + card._id}
                    className={'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-solid tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-transition-all tw-duration-200 hover:tw-border-brand-gold hover:tw--translate-y-0.5' + (isHoriz ? ' horiz-card' : '')}
                    style={{ textDecoration: 'none', display: 'block' }}
                  >
                    <div className={isHoriz ? 'horiz-card-img tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-relative' : 'tw-aspect-[3/4] tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-py-2 tw-relative'}>
                      <LazyCardImage src={imageUrl} alt={card.name} fallbackSize="tw-w-12 tw-h-12" rotate={isHoriz ? (card.rotation || -90) : 0} />
                      {card.lowestPrice != null ? (
                        <div
                          className="tw-absolute tw-bottom-2 tw-left-2 tw-text-brand-gold tw-text-base tw-font-bold tw-px-3 tw-py-1 tw-rounded-full tw-shadow-lg"
                          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
                        >
                          ${card.lowestPrice.toFixed(2)}
                        </div>
                      ) : (
                        <div
                          className="tw-absolute tw-bottom-2 tw-left-2 tw-text-xs tw-font-medium tw-px-3 tw-py-1 tw-rounded-full"
                          style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#999' }}
                        >
                          No listings
                        </div>
                      )}
                    </div>
                    <div className="tw-p-3">
                      <p className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0.5 tw-truncate">
                        {card.name}
                      </p>
                      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-x-1 tw-gap-y-0.5">
                        {card.cardSet ? (
                          <span className="tw-text-[10px] tw-text-[#999] dark:tw-text-[#666]">{card.cardSet}</span>
                        ) : <span />}
                        {renderRarityBadge(card.rarity, null)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Loading more spinner */}
          {this.state.loadingMore && (
            <div className="tw-flex tw-justify-center tw-py-8">
              <div className="tw-w-8 tw-h-8 tw-border-2 tw-border-brand-gold tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
            </div>
          )}

          {/* Initial loading state */}
          {this.state.loading && (
            <div className="tw-flex tw-justify-center tw-py-16">
              <div className="tw-w-8 tw-h-8 tw-border-2 tw-border-brand-gold tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
            </div>
          )}

          {/* All loaded indicator */}
          {!this.state.loading && !this.state.loadingMore && sortedCards.length > 0 && !hasMore && sortedCards.length > PAGE_SIZE && (
            <p className="tw-text-center tw-text-[#bbb] dark:tw-text-[#555] tw-text-xs tw-mt-8">
              Showing all {this.state.cardsTotal} results
            </p>
          )}

          {/* Empty state */}
          {!this.state.loading && this.state.cardResults.length === 0 && (
            <div className="tw-text-center tw-py-16">
              <i className="fa fa-search tw-text-4xl tw-text-[#ccc] dark:tw-text-[#444] tw-mb-4 tw-block"></i>
              <p className="tw-text-[#999] dark:tw-text-gray-500 tw-text-sm">
                No cards found for "<strong>{this.state.query}</strong>"
              </p>
              <p className="tw-text-[#bbb] dark:tw-text-[#555] tw-text-xs tw-mt-1">
                Try a different search term
              </p>
            </div>
          )}
        </div>

        {/* Scroll to top button */}
        {this.state.showScrollTop && (
          <button
            onClick={this.scrollToTop}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)', animation: 'hp-fadeUp 0.3s ease forwards' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default Search;
